import { useState } from 'react';
import { Box } from 'lucide-react';
import { useAsyncResource, useScopedNavigate } from '@hooks';
import { getAnomalies, getAnomalyReportFileName } from '@entities';
import { FileEvidence } from '@features/evidence';
import { AnomalyDetail } from '@features/anomaly-register/components';
import {
  componentKey, flagLabel, hasComponentLocation, relatedAnomalies, techniqueLabel, textValue,
} from '@/entities/component/component.model';
import { rdsColumns } from '../component.columns';
import DetailDialog from '@/shared/components/DetailDialog/DetailDialog';
import styles from './ComponentDetail.module.css';
import HistoryLink from '@/features/lifecycle-history/HistoryLink';


export default function ComponentDetail({ component, companyId, canMatchAnomalies, onClose }) {
  const navigate = useScopedNavigate();
  const [activeFinding, setActiveFinding] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const hasLocation = hasComponentLocation(component);
  const canLoad = Boolean(companyId && hasLocation && textValue(component.rds_code) && canMatchAnomalies);
  const anomalies = useAsyncResource(async () => {
    const rows = await getAnomalies({ companyId });
    if (!Array.isArray(rows)) throw new Error('The anomaly API must return a list.');
    return relatedAnomalies(rows, component);
  }, [companyId, component.site_id, component.asset_id, component.rds_code],
    { initialData: [], enabled: canLoad });


  const reports = [...new Map(anomalies.data.flatMap(anomaly => {
    const fileName = getAnomalyReportFileName(anomaly);
    return fileName ? [[fileName, { fileName, reference: textValue(anomaly.anomaly_report_id) }]] : [];
  })).values()];
  const findings = anomalies.data.map((row, index) => ({
    key: JSON.stringify([row.id ?? null, row.anomaly_ref ?? '', index]), row,
  }));
  const finding = findings.find(item => item.key === activeFinding)?.row;
  const report = reports.find(item => item.fileName === activeReport);

  function openViewer() {
    navigate('/anomaly-suite', {
      scope: { siteId: component.site_id, assetId: component.asset_id },
      params: { anomalyView: 'viewer', viewerComponent: componentKey(component), tab: null },
    });
  }

  return (
    <DetailDialog eyebrow="INSPECTION OBJECT"
      title={textValue(component.rds_code) || 'Component details'}
      subtitle={component.assetLabel + ' · ' + component.siteLabel} onClose={onClose}>
      <div className={styles.body}>
        <p className={styles.description}>{textValue(component.description) || 'No description recorded.'}</p>
        <div className={styles.actions}>
          <button type="button" className={styles.button} disabled={!hasLocation || !textValue(component.rds_code)}
            onClick={openViewer}><Box size={16} aria-hidden="true" /> Locate in 3D viewer</button>
          {!hasLocation && <p>Site and asset IDs are required to open the model.</p>}
        </div>
        <p>Eligible techniques: <strong>{techniqueLabel(component)}</strong></p>
        <p className={styles.note}>Eligibility is catalogue information; it does not confirm an assigned or completed inspection.</p>

        <section className={styles.section}>
          <h3>Object metadata</h3>
          <dl className={styles.metadata}>
            {rdsColumns.map(column => <div key={column.key}>
              <dt>{column.label}</dt>
              <dd>{column.key === 'is_root' || column.key.startsWith('in_')
                ? flagLabel(component[column.key]) : textValue(component[column.key]) || 'Not recorded'}
                {(column.key === 'is_root' || column.key.startsWith('in_')) &&
                  <small>Raw value: {textValue(component[column.key]) || 'Not recorded'}</small>}
              </dd>
            </div>)}
            {['id', 'side', 'inspection_scope'].filter(key => textValue(component[key])).map(key => (
              <div key={key}><dt>{key === 'id' ? 'Record ID' : key === 'side' ? 'Internal / external' : 'Inspection scope'}</dt>
                <dd>{textValue(component[key])}</dd></div>
            ))}
          </dl>
        </section>

        <section className={styles.section}>
          <h3>Related anomalies</h3>
          <p className={styles.note}>Matched by exact RDS code within this site and asset. This list does not apply Anomaly Suite filters.</p>
          {!canLoad ? <p>Component links cannot be resolved: a unique RDS code and complete location are required.</p>
            : anomalies.isLoading ? <p role="status">Loading related anomalies…</p>
              : anomalies.error ? <div><p role="alert">{anomalies.error}</p>
                <button type="button" className={styles.button} onClick={() => anomalies.reload()}>Retry evidence</button></div>
                : <>
                  <p>{findings.length ? `${findings.length} matching anomalies.` :
                    'No matching anomaly records. This does not establish component condition or inspection coverage.'}</p>
                  <ul className={styles.records}>{findings.map(({ key, row }) => <li key={key}>
                    <button type="button" className={styles.record} aria-expanded={activeFinding === key}
                      onClick={() => setActiveFinding(previous => previous === key ? null : key)}>
                      <strong>{textValue(row.anomaly_ref) || 'Finding without reference'}</strong>
                      <span>{textValue(row.failure_mode) || 'Failure mode not recorded'}</span>
                      <small>Severity: {textValue(row.anomaly_severity) || 'Unknown'} · Status: {textValue(row.anomaly_status) || 'Unknown'}</small>
                    </button>
                  </li>)}</ul>
                  {finding && <div className={styles.finding} key={activeFinding}><AnomalyDetail content={finding} /></div>}
                </>}
        </section>

        <section className={styles.section}>
          <h3>Reports attached to matched anomalies</h3>
          <p className={styles.note}>This is not a complete component inspection-report history.</p>
          {!canLoad ? <p>Report relationships are unavailable.</p>
            : anomalies.isLoading ? <p>Loading report references…</p>
              : anomalies.error ? <p>Report references are unavailable until anomalies load successfully.</p>
                : <>
                  {!reports.length && <p>No report references are attached to these anomalies.</p>}
                  <ul className={styles.records}>{reports.map(item => <li key={item.fileName}>
                    <button type="button" className={styles.record} aria-expanded={activeReport === item.fileName}
                      onClick={() => setActiveReport(previous => previous === item.fileName ? null : item.fileName)}>
                      {item.reference}
                    </button>
                  </li>)}</ul>
                  {report && <FileEvidence companyId={companyId} siteId={component.site_id}
                    assetId={component.asset_id} folder="reports" fileName={report.fileName} kind="report" />}
                </>}
        </section>

        <section className={styles.section}>
          <h3>Lifecycle history</h3>
          {hasLocation && textValue(component.rds_code) && canMatchAnomalies ? (
            <HistoryLink
              siteId={component.site_id}
              assetId={component.asset_id}
              componentCode={textValue(component.rds_code)}
            >
              View component record history
            </HistoryLink>
          ) : (
            <p>A complete location and unique RDS code are needed for component history links.</p>
          )}
          <p className={styles.note}>
            Dated anomaly records are available where recorded. Component inspection events,
            schedule history and status-change events are not connected yet.
          </p>
        </section>
      </div>

    </DetailDialog>
  );
}